import React, { useDeferredValue, useState } from 'react';

import { Link } from 'react-router-dom';

import { LoadingIcon, Pagination } from '@pkg/components';
import UserListActions from '@pkg/components/Sections/Admin/Users/UserListActions';
import { ADMIN_USER_CACHE, useGetUsers } from '@pkg/components/Sections/Admin/Users/hooks';
import {
    TruncatedValue,
    UserTableHeader,
    UserTableItem,
    UserTableRow,
    UserYatAmount,
} from '@pkg/components/Sections/Admin/Users/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';
import { Colors } from '@pkg/components/Sections/Profile/Buttons/ButtonBase';
import { DepStyledInput } from '@pkg/components/deprecated';
import { useQueryInvalidation } from '@pkg/hooks';
import { copyToClipboard } from '@pkg/utils';

import { type ResourceId } from '@app/client';

export const Users = () => {
    const invalidateUsers = useQueryInvalidation([ADMIN_USER_CACHE]);
    const [showAllYats, setShowAllYats] = useState<Record<ResourceId, boolean>>({});
    const [searchQuery, setSearchQuery] = useState('');
    const deferredQuery = useDeferredValue(searchQuery);

    const {
        data,
        isLoading,
        isFetching,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        pagingParams,
    } = useGetUsers({ limit: 20, query: deferredQuery });

    const usersList = data?.pages[pagingParams?.page || 0]?.data || [];

    const paginationMarkup = !isLoading && (
        <Pagination
            variant="buttoned"
            fetchNextPage={fetchNextPage}
            fetchPreviousPage={fetchPreviousPage}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            pagingParams={pagingParams}
        />
    );
    return (
        <>
            {paginationMarkup}

            <hr />
            <br />

            <DepStyledInput
                name="search"
                autoFocus
                value={searchQuery}
                placeholder={`Search users`}
                onChange={(e) => {
                    setSearchQuery(e.target.value);
                }}
            />
            <br />

            <hr />
            <UserTableRow>
                {/*headings*/}
                <UserTableHeader>Email / Primary Yat</UserTableHeader>
                <UserTableHeader>Invited by</UserTableHeader>
                <UserTableHeader>Pubkeys</UserTableHeader>
                <UserTableHeader>Added</UserTableHeader>
                <UserTableHeader>Yats</UserTableHeader>
                <UserTableHeader>Quick Actions</UserTableHeader>
            </UserTableRow>
            {/*  items*/}
            {(isLoading || isFetching) && <LoadingIcon />}
            {usersList?.length
                ? usersList.map((user) => {
                      if (user) {
                          return (
                              <UserTableRow key={user.id}>
                                  <UserTableItem>
                                      <Link to={user.id}>{user.email}</Link>
                                      <Link to={`/${user.eid}`}>{user.eid}</Link>
                                  </UserTableItem>
                                  <UserTableItem>
                                      <TruncatedValue
                                          $cursor="pointer"
                                          onClick={() => setSearchQuery(user?.inviter_email || '')}
                                          title={user.inviter_email}
                                      >
                                          {user.inviter_email}
                                      </TruncatedValue>
                                  </UserTableItem>
                                  <UserTableItem>
                                      {user.pubkeys?.map((p) => {
                                          return (
                                              <TruncatedValue
                                                  $cursor="copy"
                                                  title="Copy pubkey"
                                                  key={p}
                                                  onClick={() => copyToClipboard(p)}
                                              >
                                                  {p}
                                              </TruncatedValue>
                                          );
                                      })}
                                  </UserTableItem>
                                  <UserTableItem>{new Date(user.created_at).toLocaleDateString()}</UserTableItem>
                                  <UserTableItem>
                                      <UserYatAmount>
                                          <strong>{`${user.emoji_ids.length} Yat${
                                              user.emoji_ids.length !== 1 ? 's' : ''
                                          }`}</strong>
                                          {user.emoji_ids.length > 3 && (
                                              <Button
                                                  size={Sizes.xsmall}
                                                  color={Colors['transparent']}
                                                  onClick={() => {
                                                      showAllYats[user.id] = !showAllYats[user.id];
                                                      setShowAllYats({ ...showAllYats });
                                                  }}
                                              >
                                                  {showAllYats[user.id]
                                                      ? 'Show Less'
                                                      : `+ ${Math.max(user.emoji_ids.length - 3, 0)} more`}
                                              </Button>
                                          )}
                                      </UserYatAmount>
                                      {user.emoji_ids
                                          .filter((_, i) => (showAllYats[user.id] ? true : i < 3))
                                          .map((emojiId) => (
                                              <span key={emojiId}>
                                                  <Link
                                                      to={`/admin/yat/${encodeURIComponent(emojiId)}`}
                                                      target="_blank"
                                                      rel="noreferrer noopener"
                                                  >
                                                      {emojiId}
                                                  </Link>
                                              </span>
                                          ))}
                                  </UserTableItem>
                                  <UserTableItem>
                                      <UserListActions user={user} onUpdateComplete={() => invalidateUsers()} />
                                  </UserTableItem>
                              </UserTableRow>
                          );
                      }
                  })
                : null}
            {paginationMarkup}
        </>
    );
};
